import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  BookOpen, FileText, Plus, Pencil, Trash2, Loader2, Search, Eye,
  Shield, GraduationCap, UserCheck, FolderOpen, Settings, Monitor,
  Sparkles, ClipboardList, Video, Users, HelpCircle, Layers, Save,
  ArrowLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  BookOpen, FileText, Shield, GraduationCap, UserCheck, FolderOpen,
  Settings, Monitor, Sparkles, ClipboardList, Video, Users, HelpCircle, Layers,
};

const ROLE_LABELS = { admin: "Admin", trainer: "Formateur", candidate: "Candidat" };
const ROLE_COLORS = { admin: "#8b5cf6", trainer: "#3b82f6", candidate: "#22c55e" };

export default function DocumentationAdminTab() {
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [view, setView] = useState("list"); // list | category | edit-cat | edit-article
  const [selectedCat, setSelectedCat] = useState(null);
  const [editCat, setEditCat] = useState(null);
  const [editArticle, setEditArticle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [catsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/documentation/categories`, { headers }),
        fetch(`${API_URL}/api/documentation/stats`, { headers }),
      ]);
      if (catsRes.ok) setCategories(await catsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchArticles = async (catId) => {
    try {
      const res = await fetch(`${API_URL}/api/documentation/articles?category_id=${catId}`, { headers });
      if (res.ok) setArticles(await res.json());
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await fetch(`${API_URL}/api/documentation/seed`, { method: "POST", headers });
      if (res.ok) { toast.success("Documentation générée !"); fetchAll(); }
    } catch (err) { toast.error("Erreur"); }
    setSeeding(false);
  };

  const saveCat = async () => {
    setSaving(true);
    try {
      const method = editCat.id ? "PUT" : "POST";
      const url = editCat.id ? `${API_URL}/api/documentation/categories/${editCat.id}` : `${API_URL}/api/documentation/categories`;
      const res = await fetch(url, { method, headers, body: JSON.stringify(editCat) });
      if (res.ok) { toast.success("Catégorie enregistrée"); fetchAll(); setView("list"); }
    } catch (err) { toast.error("Erreur"); }
    setSaving(false);
  };

  const deleteCat = async (id) => {
    if (!window.confirm("Supprimer cette catégorie et tous ses articles ?")) return;
    await fetch(`${API_URL}/api/documentation/categories/${id}`, { method: "DELETE", headers });
    toast.success("Catégorie supprimée");
    fetchAll();
  };

  const saveArticle = async () => {
    setSaving(true);
    try {
      const method = editArticle.id ? "PUT" : "POST";
      const url = editArticle.id ? `${API_URL}/api/documentation/articles/${editArticle.id}` : `${API_URL}/api/documentation/articles`;
      const res = await fetch(url, { method, headers, body: JSON.stringify(editArticle) });
      if (res.ok) {
        toast.success("Article enregistré");
        if (selectedCat) { fetchArticles(selectedCat.id); setView("category"); }
        else { setView("list"); }
      }
    } catch (err) { toast.error("Erreur"); }
    setSaving(false);
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await fetch(`${API_URL}/api/documentation/articles/${id}`, { method: "DELETE", headers });
    toast.success("Article supprimé");
    if (selectedCat) fetchArticles(selectedCat.id);
  };

  const GetIcon = ({ name, ...props }) => {
    const Icon = ICON_MAP[name] || FileText;
    return <Icon {...props} />;
  };

  // ─── Edit Article View ───
  if (view === "edit-article") {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setView(selectedCat ? "category" : "list")}><ArrowLeft className="h-4 w-4" /></Button>
          <h2 className="text-xl font-bold">{editArticle?.id ? "Modifier" : "Nouvel"} article</h2>
        </div>
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Titre</Label>
                <Input value={editArticle?.title || ""} onChange={e => setEditArticle({ ...editArticle, title: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Icône</Label>
                <Select value={editArticle?.icon || "FileText"} onValueChange={v => setEditArticle({ ...editArticle, icon: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(ICON_MAP).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Résumé</Label>
              <Input value={editArticle?.summary || ""} onChange={e => setEditArticle({ ...editArticle, summary: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Contenu (Markdown)</Label>
              <Textarea rows={14} value={editArticle?.content || ""} onChange={e => setEditArticle({ ...editArticle, content: e.target.value })} className="font-mono text-sm" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Rôles cibles</Label>
                <div className="flex flex-wrap gap-2">
                  {["admin", "trainer", "candidate"].map(r => (
                    <button key={r} onClick={() => {
                      const roles = editArticle?.target_roles || [];
                      setEditArticle({ ...editArticle, target_roles: roles.includes(r) ? roles.filter(x => x !== r) : [...roles, r] });
                    }} className="px-3 py-1 rounded-full text-xs font-medium border transition-all"
                      style={{
                        background: (editArticle?.target_roles || []).includes(r) ? `${ROLE_COLORS[r]}20` : "transparent",
                        borderColor: (editArticle?.target_roles || []).includes(r) ? ROLE_COLORS[r] : "var(--border)",
                        color: (editArticle?.target_roles || []).includes(r) ? ROLE_COLORS[r] : "var(--muted-foreground)",
                      }}
                    >{ROLE_LABELS[r]}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Tags (séparés par virgule)</Label>
                <Input value={(editArticle?.tags || []).join(", ")} onChange={e => setEditArticle({ ...editArticle, tags: e.target.value.split(",").map(t => t.trim()).filter(Boolean) })} />
              </div>
              <div className="space-y-2">
                <Label>Ordre</Label>
                <Input type="number" value={editArticle?.order || 0} onChange={e => setEditArticle({ ...editArticle, order: parseInt(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={editArticle?.published ?? true} onCheckedChange={v => setEditArticle({ ...editArticle, published: v })} />
                <Label>Publié</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editArticle?.is_faq ?? false} onCheckedChange={v => setEditArticle({ ...editArticle, is_faq: v })} />
                <Label>FAQ</Label>
              </div>
            </div>
            {editArticle?.is_faq && (
              <div className="space-y-2">
                <Label>Question FAQ</Label>
                <Input value={editArticle?.faq_question || ""} onChange={e => setEditArticle({ ...editArticle, faq_question: e.target.value })} placeholder="Ex: Comment réinitialiser mon mot de passe ?" />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setView(selectedCat ? "category" : "list")}>Annuler</Button>
              <Button onClick={saveArticle} disabled={saving} className="gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Edit Category View ───
  if (view === "edit-cat") {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setView("list")}><ArrowLeft className="h-4 w-4" /></Button>
          <h2 className="text-xl font-bold">{editCat?.id ? "Modifier" : "Nouvelle"} catégorie</h2>
        </div>
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Titre</Label>
                <Input value={editCat?.title || ""} onChange={e => setEditCat({ ...editCat, title: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Icône</Label>
                <Select value={editCat?.icon || "BookOpen"} onValueChange={v => setEditCat({ ...editCat, icon: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(ICON_MAP).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={editCat?.description || ""} onChange={e => setEditCat({ ...editCat, description: e.target.value })} rows={3} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Couleur</Label>
                <Input type="color" value={editCat?.color || "#8b5cf6"} onChange={e => setEditCat({ ...editCat, color: e.target.value })} className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Label>Ordre</Label>
                <Input type="number" value={editCat?.order || 0} onChange={e => setEditCat({ ...editCat, order: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label>Rôles cibles</Label>
                <div className="flex flex-wrap gap-2 pt-1">
                  {["admin", "trainer", "candidate"].map(r => (
                    <button key={r} onClick={() => {
                      const roles = editCat?.target_roles || [];
                      setEditCat({ ...editCat, target_roles: roles.includes(r) ? roles.filter(x => x !== r) : [...roles, r] });
                    }} className="px-3 py-1 rounded-full text-xs font-medium border transition-all"
                      style={{
                        background: (editCat?.target_roles || []).includes(r) ? `${ROLE_COLORS[r]}20` : "transparent",
                        borderColor: (editCat?.target_roles || []).includes(r) ? ROLE_COLORS[r] : "var(--border)",
                        color: (editCat?.target_roles || []).includes(r) ? ROLE_COLORS[r] : "var(--muted-foreground)",
                      }}
                    >{ROLE_LABELS[r]}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setView("list")}>Annuler</Button>
              <Button onClick={saveCat} disabled={saving} className="gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Category Detail View ───
  if (view === "category" && selectedCat) {
    const catArticles = articles.filter(a => a.category_id === selectedCat.id);
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => { setView("list"); setSelectedCat(null); }}><ArrowLeft className="h-4 w-4" /></Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${selectedCat.color}20` }}>
                <GetIcon name={selectedCat.icon} className="h-5 w-5" style={{ color: selectedCat.color }} />
              </div>
              <div>
                <h2 className="text-xl font-bold">{selectedCat.title}</h2>
                <p className="text-sm text-muted-foreground">{catArticles.length} article(s)</p>
              </div>
            </div>
          </div>
          <Button className="gap-2" onClick={() => {
            setEditArticle({ category_id: selectedCat.id, title: "", content: "", summary: "", icon: "FileText", tags: [], target_roles: selectedCat.target_roles || [], is_faq: false, faq_question: "", steps: [], order: catArticles.length + 1, published: true });
            setView("edit-article");
          }}>
            <Plus className="h-4 w-4" /> Nouvel article
          </Button>
        </div>
        <div className="space-y-3">
          {catArticles.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground"><FileText className="h-8 w-8 mx-auto mb-2 opacity-30" /><p>Aucun article</p></CardContent></Card>
          ) : catArticles.map(a => (
            <Card key={a.id} className="hover:shadow-md transition-shadow">
              <CardContent className="flex items-center gap-4 py-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-muted">
                  <GetIcon name={a.icon} className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{a.title}</p>
                    {a.is_faq && <Badge variant="secondary" className="text-[10px]">FAQ</Badge>}
                    {!a.published && <Badge variant="outline" className="text-[10px] text-amber-500 border-amber-300">Brouillon</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{a.summary}</p>
                  <div className="flex gap-1 mt-1">
                    {(a.target_roles || []).map(r => (
                      <span key={r} className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: `${ROLE_COLORS[r]}15`, color: ROLE_COLORS[r] }}>{ROLE_LABELS[r]}</span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditArticle(a); setView("edit-article"); }}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteArticle(a.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // ─── Main List View ───
  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: "Catégories", value: stats.categories, color: "#8b5cf6", icon: Layers },
            { label: "Articles", value: stats.articles, color: "#3b82f6", icon: FileText },
            { label: "FAQ", value: stats.faq, color: "#f59e0b", icon: HelpCircle },
            { label: "Publiés", value: stats.published, color: "#22c55e", icon: Eye },
            { label: "Brouillons", value: stats.drafts, color: "#94a3b8", icon: Pencil },
          ].map(s => (
            <Card key={s.label} className="overflow-hidden">
              <CardContent className="flex items-center gap-3 py-3 px-4">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${s.color}15` }}>
                  <s.icon className="h-4 w-4" style={{ color: s.color }} />
                </div>
                <div>
                  <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
                  <p className="text-[10px] text-muted-foreground">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" /> Catégories
        </h2>
        <div className="flex gap-2">
          {categories.length === 0 && (
            <Button variant="outline" onClick={handleSeed} disabled={seeding} className="gap-2">
              {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Générer la documentation
            </Button>
          )}
          <Button className="gap-2" onClick={() => {
            setEditCat({ title: "", description: "", icon: "BookOpen", color: "#8b5cf6", target_roles: ["admin", "trainer", "candidate"], order: categories.length + 1 });
            setView("edit-cat");
          }}>
            <Plus className="h-4 w-4" /> Nouvelle catégorie
          </Button>
        </div>
      </div>

      {/* Category Cards */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : categories.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <BookOpen className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="font-semibold mb-2">Aucune documentation</h3>
            <p className="text-sm text-muted-foreground text-center mb-4">Cliquez sur "Générer la documentation" pour créer le contenu initial</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map(cat => (
            <Card key={cat.id} className="group hover:shadow-lg transition-all cursor-pointer overflow-hidden" onClick={() => { setSelectedCat(cat); fetchArticles(cat.id); setView("category"); }}>
              <div className="h-2" style={{ background: cat.color }} />
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-sm" style={{ background: `${cat.color}15` }}>
                      <GetIcon name={cat.icon} className="h-6 w-6" style={{ color: cat.color }} />
                    </div>
                    <div>
                      <h3 className="font-semibold">{cat.title}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">{cat.description}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
                </div>
                <div className="flex items-center gap-2 mt-3">
                  {(cat.target_roles || []).map(r => (
                    <span key={r} className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${ROLE_COLORS[r]}15`, color: ROLE_COLORS[r] }}>{ROLE_LABELS[r]}</span>
                  ))}
                </div>
              </CardContent>
              {/* Action buttons */}
              <div className="flex border-t px-4 py-2 gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => { setEditCat(cat); setView("edit-cat"); }}>
                  <Pencil className="h-3 w-3" /> Modifier
                </Button>
                <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs text-destructive" onClick={() => deleteCat(cat.id)}>
                  <Trash2 className="h-3 w-3" /> Supprimer
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
